import React, {useState, useEffect} from 'react';
import DataTable from "../../../components/DataTable";
import data from "./data/dataTableData";
import theme from "../../../assets/theme";
import ExpandableComponent from "./components/ExpandableComponent";
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import {ReturnOrderController} from "../../../controllers/returnOrderController";

interface RefundsDataType {
    data: any[];
    totalPages: number;
    totalElements: number;
    last: boolean;
    first: boolean;
    hasNext: boolean;
    hasPrevious: boolean;
    numberOfElements: number;
}

interface ApiResponse {
    status: number;
    message: string | null;
    errors: any | null;
    data: RefundsDataType;
    timeStamp: number;
}

// Human-readable label for the refund destination the customer chose.
const destinationLabel = (d?: string) => {
    switch (d) {
        case "WALLET":
            return "Wallet";
        case "ORIGINAL_PAYMENT":
            return "Original payment";
        case "BANK":
            return "Bank";
        case "UPI":
            return "UPI";
        default:
            return "—";
    }
};

function Refunds() {
    const {typography} = theme;
    const [refundsData, setRefundsData] = useState<RefundsDataType>({
        data: [],
        totalPages: 0,
        totalElements: 0,
        last: true,
        first: true,
        hasNext: false,
        hasPrevious: false,
        numberOfElements: 0,
    });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const fetchRefundsData = async () => {
        setLoading(true);
        try {
            const response = await ReturnOrderController.getMerchantRefunds({page, size: pageSize});
            const apiResponse = response.data as ApiResponse;
            if (apiResponse && apiResponse.data) {
                setRefundsData(apiResponse.data);
            }
        } catch (error) {
            console.error("Error fetching refunds data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRefundsData();
    }, [page, pageSize]);

    const tableData = {
        columns: data.columns,
        rows: (refundsData.data || []).map((r: any) => ({
            productName: r.productName ?? '—',
            amount: r.amount != null ? `₹${r.amount}` : '—',
            destination: destinationLabel(r.refundDestination),
            status: r.status ?? '—',
            date: r.createdAt ? String(r.createdAt).slice(0, 10) : '—',
            details: {
                refundType: r.refundType,
                reason: r.reason,
                paymentMode: r.paymentMode,
                destination: destinationLabel(r.refundDestination),
                quantity: r.quantity,
                razorpayOrderId: r.razorpayOrderId,
                razorpayRefundId: r.razorpayRefundId,
                customerId: r.customerId,
            },
        })),
    };

    return (
        <FaiBox className={"refunds"}>
            <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                Refunds
            </FaiTypography>
            <FaiBox className={"refunds-content-body"} mt={3}>
                {loading ? (
                    <FaiTypography>Loading...</FaiTypography>
                ) : refundsData.numberOfElements === 0 ? (
                    <FaiBox sx={{textAlign: 'center', padding: '20px'}}>
                        <FaiTypography variant="body1">No refunds yet.</FaiTypography>
                    </FaiBox>
                ) : (
                    <DataTable
                        showHeader={false}
                        table={tableData}
                        expandRows={true}
                        renderRowSubComponent={ExpandableComponent}
                        expandedRowObj={tableData}
                        pagination={{
                            variant: "contained",
                            color: "primary",
                        }}
                    />
                )}
            </FaiBox>
        </FaiBox>
    );
}

export default Refunds;
